import { getSearchParams } from "../../../utils/request";
import { api } from "../../api";

const manifestsApi = api.injectEndpoints({
  tagTypes: ["Manifests"],
  endpoints: (builder) => ({
    getManifests: builder.query({
      query: ({ page = 1, limit = 20, employeeId, ...rest }) => {
        // Transform employeeId filter to handle organizations
        // Organizations are prefixed with "org_"
        let transformedParams = { ...rest };
        
        if (employeeId) {
          // Split comma-separated values
          const ids = Array.isArray(employeeId) 
            ? employeeId 
            : employeeId.split(",").filter(Boolean);
          
          const organizationIds = [];
          const employeeIds = [];
          
          ids.forEach((id) => {
            if (id.startsWith("org_")) {
              organizationIds.push(id.replace("org_", ""));
            } else {
              employeeIds.push(id);
            }
          });
          
          // If we have organization IDs, use organizationId param
          // Backend handles organizationId by finding all employees in that org
          if (organizationIds.length > 0) {
            // If multiple organizations, use the first one (backend limitation)
            transformedParams.organizationId = organizationIds[0];
          }
          
          // If we have employee IDs, use employeeId param (will be handled as driverId in backend)
          if (employeeIds.length > 0) {
            transformedParams.employeeId = employeeIds.join(",");
          }
        }
        
        const params = new URLSearchParams({
          page,
          limit,
          legacy: false,
          ...transformedParams,
        });
        return `manifests?${params}`;
      },
      transformResponse: (response) => {
        // Normalize legacy + new pagination shapes for callers
        const payload = response?.data || response;
        if (!payload) return payload;

        const manifests =
          payload.manifests ||
          payload.docs ||
          payload.data?.manifests ||
          payload.data?.docs ||
          [];

        if (Array.isArray(manifests)) {
          return {
            ...payload,
            manifests,
            // Keep docs for callers that rely on it
            docs: payload.docs || manifests,
          };
        }

        return payload;
      },
      providesTags: (data) =>
        data?.manifests
          ? [
              ...data.manifests.map(({ _id }) => ({
                type: "Manifests",
                id: _id,
              })),
              { type: "Manifests", id: "LIST" },
            ]
          : [{ type: "Manifests", id: "LIST" }],
    }),
    addManifest: builder.mutation({
      query(body) {
        return {
          url: "manifests",
          method: "POST",
          body,
        };
      },
      invalidatesTags: [{ type: "Manifests", id: "LIST" }],
    }),
    getManifest: builder.query({
      query: ({ id, ...rest }) => `manifests/${id}?${getSearchParams(rest)}`,
      transformResponse: (response) => response.data,
      providesTags: (_result, _error, data) => {
        return [{ type: "Manifests", id: data.id }];
      },
    }),
    updateManifest: builder.mutation({
      query({ id, data }) {
        return {
          url: `/manifests/${id}`,
          method: "PUT",
          body: data,
        };
      },
      invalidatesTags: (result, _error, { id }) => {
        const jobs = result.data.legs
          .filter((i) => i.jobId)
          .map((item) => ({
            type: "Jobs",
            id: item.jobId?._id,
          }));

        return [
          ...jobs,
          { type: "Legs", id: "LIST" },
          { type: "Manifests", id },
        ];
      },
    }),
    updateManifestLeg: builder.mutation({
      query({ data, id, legId, ...obj }) {
        const params = getSearchParams(obj);
        return {
          url: `/manifests/${id}/leg/${legId}?${params}`,
          method: "PUT",
          body: data,
        };
      },
      invalidatesTags: (result, _error, data) => {
        const jobs = result.data.legs
          .filter((i) => i.jobId)
          .map((item) => ({
            type: "Jobs",
            id: item.jobId?._id,
          }));

        return [
          ...jobs,
          { type: "Legs", id: "LIST" },
          { type: "Manifests", id: data.id },
        ];
      },
    }),

    deleteManifests: builder.mutation({
      query({ data }) {
        return {
          url: "/manifests",
          method: "DELETE",
          body: data,
        };
      },
      invalidatesTags: (_result, _error, { data }) => [
        ...data.map((id) => ({ type: "Manifests", id })),
        { type: "Payouts", id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetManifestsQuery,
  useDeleteManifestsMutation,
  useAddManifestMutation,
  useGetManifestQuery,
  useLazyGetManifestQuery,
  useUpdateManifestLegMutation,
  useUpdateManifestMutation,
} = manifestsApi;
